import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Plus, PencilSimple, Trash, Copy, PaperPlaneTilt, Buildings, LinkSimple,
  Calendar, ArrowsClockwise, ChatCenteredText, CaretDown, CaretRight, CheckCircle,
  XCircle, ArrowUUpLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// Small "5m ago" formatter — good enough for the sync-status pill.
function timeAgo(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "?";
  const s = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (s < 60)    return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60)   return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

// Discovery status → { label, tone, help }
const DISCOVERY_META = {
  "": { label: "No discovery", tone: "slate", help: "Legacy client — no discovery workflow attached." },
  discovery_requested:    { label: "Discovery Requested",    tone: "amber",   help: "Book the meeting, then click Schedule Discovery." },
  discovery_scheduled:    { label: "Discovery Scheduled",    tone: "blue",    help: "Waiting for presale to submit notes in PSDoc." },
  notes_sent_for_review:  { label: "Notes Received",         tone: "emerald", help: "Presale submitted notes — create an intake link and email it to the client via Outlook." },
  awaiting_client_reply:  { label: "Awaiting Client Reply",  tone: "amber",   help: "Intake link has been emailed — waiting for the client to submit." },
  discovery_confirmed:    { label: "Confirmed by client",    tone: "violet",  help: "Client submitted the intake — awaiting the PSDoc engineer to pick it up." },
  sow_in_progress:        { label: "SOW in progress",        tone: "indigo",  help: "PSDoc engineer has started work on the SOW/BOM. HLD/LLD in progress." },
  discovery_completed:    { label: "Discovery Completed",    tone: "slate",   help: "PSDoc closed the discovery job; SOW draft underway." },
  sow_ready:              { label: "SOW Ready",              tone: "violet",  help: "PSDoc submitted HLD/LLD/SOW/BOM. Proposal is ready to review on the Proposals tab." },
  proposal_approved:      { label: "Proposal Approved",      tone: "emerald", help: "Admin approved the proposal — ready to issue the signing link." },
  awaiting_signature:     { label: "Awaiting Signature",     tone: "amber",   help: "Signing link issued to the customer." },
  signed:                 { label: "Signed (Closed Won)",    tone: "emerald", help: "Customer signed. Milestones auto-created. Config-gen phase starts." },
  config_in_progress:     { label: "Config In Progress",     tone: "blue",    help: "PSDoc / netconfig-gen is generating device configs." },
  deployment:             { label: "Deployment",             tone: "blue",    help: "Field deployment underway." },
  delivered:              { label: "Delivered",              tone: "slate",   help: "Deployment complete — invoicing on schedule." },
  discovery_cancelled:    { label: "Discovery Cancelled",    tone: "red",     help: "Discovery was cancelled. Click Reactivate to bring it back." },
};

const TONE = {
  slate:   "text-slate-600 bg-slate-100 border-slate-300",
  amber:   "text-amber-700 bg-amber-50 border-amber-200",
  blue:    "text-blue-700 bg-blue-50 border-blue-200",
  emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
  violet:  "text-violet-700 bg-violet-50 border-violet-200",
  indigo:  "text-indigo-700 bg-indigo-50 border-indigo-200",
  red:     "text-red-700 bg-red-50 border-red-200",
};

const CANCEL_CATEGORIES = [
  { key: "customer_withdrew", label: "Customer withdrew" },
  { key: "bad_fit",           label: "Bad fit / can't help" },
  { key: "budget",            label: "Budget issue" },
  { key: "duplicate",         label: "Duplicate deal" },
  { key: "other",             label: "Other" },
];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(init());

  // Send intake dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteForm, setInviteForm] = useState({ project_title: "", note: "" });

  // Schedule discovery dialog
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ meeting_at: "", note: "" });

  // Cancel discovery dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelForm, setCancelForm] = useState({ reason: "", category: "customer_withdrew" });

  // Discovery notes drawer (per-client expand)
  const [expandedNotes, setExpandedNotes] = useState({});
  const [timelineOpen, setTimelineOpen] = useState({});
  const [timelines, setTimelines] = useState({});

  const toggleTimeline = async (c) => {
    const isOpen = !!timelineOpen[c.id];
    setTimelineOpen({ ...timelineOpen, [c.id]: !isOpen });
    if (!isOpen && !timelines[c.id]) {
      try {
        const r = await api.get(`/clients/${c.id}/timeline`);
        setTimelines((t) => ({ ...t, [c.id]: r.data.timeline || [] }));
      } catch (e) {
        toast.error("Failed to load timeline");
      }
    }
  };

  const fmtTimelineKind = (kind) => {
    const map = {
      "intake:agree":       "Customer agreed",
      "intake:disagree":    "Customer disagreed",
      "status:awaiting_client_reply": "Intake link sent to customer",
      "status:discovery_confirmed":   "Customer confirmed intake",
      "status:revision_requested":    "Revision requested",
      "status:discovery_cancelled":   "Discovery cancelled",
      "status:proposal_approved":     "Proposal approved",
      "discovery_notes_ready":        "Presale submitted notes",
      "project_created":              "PSDoc created the project",
      "sow_started":                  "PSDoc engineer started the SOW",
      "assessment_ready":             "PSDoc assessment ready",
      "sow_ready":                    "PSDoc submitted SOW/BOM",
      "config_started":               "Config generation started",
      "config_passed":                "Config passed",
      "config_failed":                "Config failed",
      "deployment_ready":             "Deployment ready",
      "won_ack":                      "PSDoc acknowledged won",
    };
    return map[kind] || kind;
  };
  const [retrying, setRetrying] = useState({});

  function init() { return { company_name: "", primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "", address: "", industry: "", notes: "" }; }

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/clients"), api.get("/invitations")]);
    setClients(c.data); setInvitations(i.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(init()); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete client? Existing invitations & proposals remain.")) return;
    await api.delete(`/clients/${id}`); toast.success("Deleted"); load();
  };

  // ---- Discovery actions ----
  const openScheduleDiscovery = (c) => {
    setScheduleFor(c);
    setScheduleForm({ meeting_at: "", note: "" });
    setScheduleOpen(true);
  };
  const submitScheduleDiscovery = async () => {
    try {
      const r = await api.post(`/clients/${scheduleFor.id}/schedule-discovery`, scheduleForm);
      const push = r.data?.psdoc_push || {};
      if (push.ok && !push.skipped) toast.success("Scheduled — discovery job created in PSDoc");
      else if (push.skipped) toast.warning("Scheduled — but PSDoc is not configured (skipped)");
      else toast.error(`Scheduled locally, but PSDoc push failed: ${push.error || "unknown"}`);
      setScheduleOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule");
    }
  };
  const retryPush = async (c) => {
    try {
      const r = await api.post(`/clients/${c.id}/retry-discovery-push`);
      if (r.data?.ok && !r.data?.skipped) toast.success("Discovery job pushed to PSDoc");
      else toast.error(`Push failed: ${r.data?.error || "unknown"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };
  const refreshDiscovery = async (c) => {
    try {
      const r = await api.post(`/clients/${c.id}/refresh-discovery`);
      if (!r.data?.ok) { toast.error(r.data?.error || "Poll failed"); return; }
      const applied = r.data?.applied || {};
      if (applied.discovery_status) toast.success(`Updated — status is now "${applied.discovery_status}"`);
      else toast.info(`No change — PSDoc still reports "${r.data.psdoc_status || 'unknown'}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    }
  };

  // Per-deal retry (fixes any pending sync for THIS client) — used by the small
  // "retry now" link on each card next to the "last synced 5m ago" indicator.
  const retrySync = async (c) => {
    setRetrying(s => ({ ...s, [c.id]: true }));
    try {
      const r = await api.post(`/clients/${c.id}/retry-sync`);
      const actions = r.data?.actions || [];
      const ok = actions.some(a => a.ok);
      const nothing = actions.length === 1 && actions[0].action === "nothing_to_do";
      if (nothing) toast.info("Nothing pending for this deal.");
      else if (ok) toast.success(`Retry complete — ${actions.map(a => a.action).join(", ")}`);
      else toast.warning(`Retried — some steps still failed. Check the dashboard.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(s => ({ ...s, [c.id]: false }));
    }
  };

  // ---- Intake link creation (NO email — admin copies + shares via Outlook) ----
  const openInvite = (c) => { setInviteFor(c); setInviteForm({ project_title: c.discovery_project_title || "", note: "" }); setInviteOpen(true); };
  const sendInvite = async () => {
    try {
      await api.post("/invitations", { client_id: inviteFor.id, ...inviteForm });
      toast.success("Intake link created — copy it from the client card");
      setInviteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    }
  };
  const buildLink = (token) => `${window.location.origin}/intake/${token}`;
  const copyLink = (token) => {
    navigator.clipboard.writeText(buildLink(token));
    toast.success("Link copied to clipboard");
  };

  // Mark the client as "awaiting reply" — call after admin sends the link via Outlook
  const markIntakeSent = async (c) => {
    if (!window.confirm(`Confirm: you've emailed the intake link to ${c.company_name} via Outlook?\n\nThis flips the status to "Awaiting Client Reply" and notifies PSDoc.`)) return;
    try {
      const r = await api.post(`/clients/${c.id}/mark-intake-sent`);
      const push = r.data?.psdoc_push || {};
      if (push.ok && !push.skipped) toast.success('Marked as sent — PSDoc updated');
      else if (push.skipped) toast.warning('Marked as sent — PSDoc not configured');
      else toast.warning(`Marked locally — PSDoc mirror failed: ${push.error || 'unknown'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark as sent');
    }
  };

  const invitesByClient = useMemo(() => {
    const m = {};
    invitations.forEach(i => { (m[i.client_id] ||= []).push(i); });
    // sort each client's invites newest first
    Object.values(m).forEach(arr => arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    return m;
  }, [invitations]);

  // Gate: Send Intake is only enabled when presale notes have been received.
  // Legacy clients (created before this workflow — empty discovery_status) keep the
  // direct-send escape hatch so nothing you built pre-Feb 2026 gets stranded.
  const canSendIntake = (c) => {
    const ds = c.discovery_status || "";
    if (!ds) return true;
    return ds === "notes_sent_for_review" || ds === "awaiting_client_reply" || ds === "discovery_confirmed";
  };
  // Schedule Discovery is available at the very start of the flow:
  //   • explicitly marked discovery_requested (kicked off from a Lead)
  //   • or legacy clients (empty status) — lets you pull them into the discovery flow.
  const canSchedule = (c) => {
    const ds = c.discovery_status || "";
    return ds === "discovery_requested" || ds === "";
  };
  // "Mark Intake Sent" — appears once presale notes have arrived AND we've
  // created at least one link (i.e. there's actually something to send).
  const hasOpenInvite = (c) => (invitesByClient[c.id] || []).some(i => i.status !== "submitted");
  const canMarkIntakeSent = (c) => (c.discovery_status || "") === "notes_sent_for_review" && hasOpenInvite(c);
  const canRetryPush = (c) => (c.discovery_status || "") === "discovery_scheduled" && !c.psdoc_discovery_id;
  const canRefresh   = (c) => ["discovery_scheduled", "notes_sent_for_review", "awaiting_client_reply"].includes(c.discovery_status || "");
  // Cancel Discovery is available at any pre-completion stage (including after
  // the customer has confirmed intake, in case they change their mind) —
  // replaces the old "delete lead" hack. Only unavailable once discovery is
  // fully completed by PSDoc or already cancelled.
  const canCancelDiscovery = (c) => {
    const ds = c.discovery_status || "";
    return ["discovery_requested", "discovery_scheduled", "notes_sent_for_review", "awaiting_client_reply", "discovery_confirmed"].includes(ds);
  };
  const canReactivate = (c) => (c.discovery_status || "") === "discovery_cancelled";

  const openCancelDiscovery = (c) => {
    setCancelFor(c);
    setCancelForm({ reason: "", category: "customer_withdrew" });
    setCancelOpen(true);
  };
  const submitCancelDiscovery = async () => {
    if (!cancelForm.reason.trim()) { toast.error("Please add a reason so the audit log is complete."); return; }
    try {
      const r = await api.post(`/clients/${cancelFor.id}/cancel-discovery`, cancelForm);
      const push = r.data?.psdoc_push || {};
      if (push.ok && !push.skipped) toast.success("Cancelled — PSDoc mirrored");
      else if (push.skipped) toast.warning("Cancelled locally — PSDoc not configured");
      else toast.warning(`Cancelled locally — PSDoc push failed: ${push.error || "unknown"}`);
      setCancelOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };
  const reactivateDiscovery = async (c) => {
    if (!window.confirm(`Reactivate discovery for ${c.company_name}? This restores the previous status.`)) return;
    try {
      await api.post(`/clients/${c.id}/reactivate-discovery`);
      toast.success("Discovery reactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reactivate failed");
    }
  };

  return (
    <div>
      <PageHeader
        overline="Client CRM"
        title="Clients"
        subtitle="Discovery workflow → intake → proposal. Intake links are generated for you to share via Outlook — no auto-emails."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-client-btn" onClick={openCreate} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-mono uppercase tracking-wider text-xs"><Plus size={14} className="mr-2"/> New Client</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Company Name</Label><Input data-testid="client-company" value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div><Label>Primary Contact</Label><Input value={form.primary_contact_name} onChange={(e) => setForm({...form, primary_contact_name: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div><Label>Email</Label><Input value={form.primary_contact_email} onChange={(e) => setForm({...form, primary_contact_email: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                <div><Label>Phone</Label><Input value={form.primary_contact_phone} onChange={(e) => setForm({...form, primary_contact_phone: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
              </div>
              <DialogFooter><Button data-testid="save-client-btn" onClick={save} className="bg-[#FFC107] hover:bg-[#E5A906]">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {clients.map((c) => {
          const invs = invitesByClient[c.id] || [];
          const submitted = invs.filter(i => i.status === "submitted").length;
          const ds = c.discovery_status || "";
          const meta = DISCOVERY_META[ds] || DISCOVERY_META[""];
          const notesExpanded = !!expandedNotes[c.id];

          return (
            <div key={c.id} data-testid={`client-card-${c.id}`} className="tactical-card border border-slate-200 bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
                    <Buildings size={16} className="text-[#0F1E38]" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg text-[#0F1E38] leading-tight">{c.company_name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{c.industry || "—"}</div>
                      {c.ps_code && (
                        <button
                          type="button"
                          data-testid={`client-ps-code-${c.id}`}
                          onClick={() => { navigator.clipboard.writeText(c.ps_code); toast.success(`Copied ${c.ps_code}`); }}
                          title="Deal reference (shared with PSDoc). Click to copy."
                          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100"
                        >
                          {c.ps_code}
                        </button>
                      )}
                      {c.discovery_bench === "green" && (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200" title="New build — greenfield">green</span>
                      )}
                      {c.discovery_bench === "brown" && (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" title="Touching existing environment — brownfield">brown</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`client-edit-${c.id}`} onClick={() => openEdit(c)} className="text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}><PencilSimple size={14} /></button>
                  {/* Delete removed — projects/deals are marked as Lost instead so nothing gets orphaned. */}
                </div>
              </div>

              <div className="mt-3 text-xs text-slate-500 space-y-0.5 font-mono">
                <div>{c.primary_contact_name}</div>
                <div className="text-slate-500">{c.primary_contact_email}</div>
                <div className="text-slate-500">{c.primary_contact_phone}</div>
              </div>

              {/* Discovery status band */}
              <div className="mt-4 pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      data-testid={`client-discovery-status-${c.id}`}
                      title={meta.help}
                      className={`text-[10px] font-mono uppercase px-2 py-1 rounded border ${TONE[meta.tone]}`}
                    >
                      {meta.label}
                      {/* Revision round suffix — Feb 2026. When the customer has
                          already gone through 1+ revisions and the intake link
                          has been re-sent, the plain "Awaiting Client Reply"
                          label was ambiguous. Show "· Round N" on any
                          revision-relevant status so admins instantly know
                          which round they're on. */}
                      {c.discovery_revision_count > 0 &&
                        (c.discovery_status === "awaiting_client_reply" ||
                         c.discovery_status === "notes_revised_for_review" ||
                         c.discovery_status === "notes_sent_for_review") &&
                        <span className="ml-1 font-bold"> · Round {c.discovery_revision_count + 1}</span>}
                    </span>
                    {c.discovery_revision_count > 0 && (
                      <span
                        data-testid={`client-revision-round-${c.id}`}
                        title="Number of revision rounds requested by the customer on the discovery notes."
                        className="text-[10px] font-mono uppercase text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1 rounded"
                      >
                        Revised · Round {c.discovery_revision_count}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {canSchedule(c) && (
                      <Button
                        size="sm"
                        onClick={() => openScheduleDiscovery(c)}
                        data-testid={`schedule-discovery-${c.id}`}
                        className="h-7 text-[10px] font-mono uppercase tracking-wider bg-blue-500/10 border border-blue-500/40 text-blue-700 hover:bg-blue-500/20"
                      >
                        <Calendar size={11} className="mr-1" /> Schedule Discovery
                      </Button>
                    )}
                    {canRetryPush(c) && (
                      <Button
                        size="sm"
                        onClick={() => retryPush(c)}
                        data-testid={`retry-discovery-push-${c.id}`}
                        title="Re-push discovery job to PSDoc"
                        className="h-7 text-[10px] font-mono uppercase tracking-wider bg-amber-500/10 border border-amber-500/40 text-amber-700 hover:bg-amber-500/20"
                      >
                        <ArrowsClockwise size={11} className="mr-1" /> Retry Push
                      </Button>
                    )}
                    {canRefresh(c) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => refreshDiscovery(c)}
                        data-testid={`refresh-discovery-${c.id}`}
                        title="Poll PSDoc for the latest discovery status"
                        className="h-7 text-[10px] font-mono uppercase tracking-wider border-slate-300"
                      >
                        <ArrowsClockwise size={11} className="mr-1" /> Refresh
                      </Button>
                    )}
                    {canMarkIntakeSent(c) && (
                      <Button
                        size="sm"
                        onClick={() => markIntakeSent(c)}
                        data-testid={`mark-intake-sent-${c.id}`}
                        title="Confirm you've emailed the intake link via Outlook — pushes 'Awaiting Client Reply' to PSDoc"
                        className="h-7 text-[10px] font-mono uppercase tracking-wider bg-violet-500/10 border border-violet-500/40 text-violet-700 hover:bg-violet-500/20"
                      >
                        <PaperPlaneTilt size={11} className="mr-1" /> Mark Sent
                      </Button>
                    )}
                    {canCancelDiscovery(c) && (
                      <Button
                        size="sm"
                        onClick={() => openCancelDiscovery(c)}
                        data-testid={`cancel-discovery-${c.id}`}
                        title="Cancel this discovery job (records a reason + mirrors to PSDoc)"
                        className="h-7 text-[10px] font-mono uppercase tracking-wider bg-red-500/10 border border-red-500/40 text-red-700 hover:bg-red-500/20"
                      >
                        <XCircle size={11} className="mr-1" /> Cancel
                      </Button>
                    )}
                    {canReactivate(c) && (
                      <Button
                        size="sm"
                        onClick={() => reactivateDiscovery(c)}
                        data-testid={`reactivate-discovery-${c.id}`}
                        title="Undo the cancellation — restores the previous status"
                        className="h-7 text-[10px] font-mono uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/20"
                      >
                        <ArrowUUpLeft size={11} className="mr-1" /> Reactivate
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => openInvite(c)}
                      disabled={!canSendIntake(c)}
                      data-testid={`invite-btn-${c.id}`}
                      title={canSendIntake(c) ? "Create an intake link to share via Outlook" : "Send intake unlocks once presale submits discovery notes"}
                      className={`h-7 text-[10px] font-mono uppercase tracking-wider ${canSendIntake(c) ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/20" : "bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed"}`}
                    >
                      <PaperPlaneTilt size={11} className="mr-1" /> Send Intake
                    </Button>
                  </div>
                </div>
                {c.psdoc_discovery_error && (
                  <div className="mt-2 text-[10px] font-mono text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                    PSDoc error: {c.psdoc_discovery_error}
                  </div>
                )}
                {c.discovery_status === "discovery_cancelled" && (
                  <div className="mt-2 text-[10px] font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid={`cancel-info-${c.id}`}>
                    Cancelled {c.discovery_cancelled_at ? timeAgo(c.discovery_cancelled_at) : ""} · {c.discovery_cancel_category || "other"}
                    {c.discovery_cancel_reason && <div className="text-red-600/80 normal-case mt-0.5">"{c.discovery_cancel_reason}"</div>}
                  </div>
                )}
                {c.discovery_revision_count > 0 && c.discovery_status === "notes_sent_for_review" && (
                  <div className="mt-2 text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`revision-info-${c.id}`}>
                    Revision requested by customer · round {c.discovery_revision_count}
                    {c.discovery_customer_feedback && <div className="text-amber-600/80 normal-case mt-0.5">"{c.discovery_customer_feedback}"</div>}
                  </div>
                )}
                {c.discovery_tech_needs_review && (
                  <div className="mt-2 text-[10px] font-mono text-red-800 bg-red-50 border-2 border-red-300 rounded px-2 py-1.5 font-bold" data-testid={`tech-review-flag-${c.id}`}>
                    ⚠ CUSTOMER FLAGGED SPEC CHANGES — verify intake before generating the proposal.
                  </div>
                )}
                {c.discovery_tech_feedback && Object.entries(c.discovery_tech_feedback).some(([k, v]) => v && String(v).trim()) && (
                  <div className="mt-2 text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`tech-feedback-${c.id}`}>
                    <div className="font-bold mb-1">Customer flagged spec changes:</div>
                    {Object.entries(c.discovery_tech_feedback).filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
                      <div key={k} className="normal-case">
                        <span className="uppercase text-amber-600">{k.replace(/_/g, " ")}:</span> "{v}"
                      </div>
                    ))}
                  </div>
                )}
                {c.psdoc_discovery_url && (
                  <a
                    href={c.psdoc_discovery_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono text-blue-700 hover:underline"
                  >
                    <LinkSimple size={11} /> Open in PSDoc
                  </a>
                )}
                {/* Per-deal sync status — surfaces stale/failed syncs before they pile up */}
                {(c.discovery_status || "") !== "" && (
                  <div
                    data-testid={`sync-status-${c.id}`}
                    className={`mt-2 flex items-center justify-between gap-2 text-[10px] font-mono px-2 py-1 rounded border ${
                      c.last_sync_at
                        ? c.last_sync_ok
                          ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                          : "text-red-700 bg-red-50 border-red-200"
                        : "text-slate-500 bg-slate-50 border-slate-200"
                    }`}
                    title={c.last_sync_error || c.last_sync_kind || ""}
                  >
                    <span className="truncate">
                      {c.last_sync_at
                        ? (c.last_sync_ok
                            ? <>✓ Last sync {timeAgo(c.last_sync_at)}{c.last_sync_kind && <span className="text-emerald-600/70"> · {c.last_sync_kind}</span>}</>
                            : <>⚠ Sync failed {timeAgo(c.last_sync_at)}{c.last_sync_kind && <span className="text-red-600/70"> · {c.last_sync_kind}</span>}</>)
                        : <>Not synced with PSDoc yet</>}
                    </span>
                    <button
                      onClick={() => retrySync(c)}
                      disabled={!!retrying[c.id]}
                      data-testid={`retry-sync-${c.id}`}
                      className="underline uppercase tracking-wider disabled:opacity-50"
                    >
                      {retrying[c.id] ? "Retrying…" : "Retry now"}
                    </button>
                  </div>
                )}
              </div>

              {/* Presale notes (once received) */}
              {(c.discovery_notes || (c.discovery_questions || []).length > 0) && (
                <div className="mt-3">
                  <button
                    onClick={() => setExpandedNotes(s => ({ ...s, [c.id]: !s[c.id] }))}
                    data-testid={`toggle-discovery-notes-${c.id}`}
                    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#0F1E38]"
                  >
                    {notesExpanded ? <CaretDown size={11} /> : <CaretRight size={11} />}
                    <ChatCenteredText size={11} /> Presale Notes {c.discovery_questions?.length ? `· ${c.discovery_questions.length} question(s)` : ""}
                  </button>
                  {notesExpanded && (
                    <div className="mt-2 p-3 rounded bg-slate-50 border border-slate-200 text-xs text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {c.discovery_notes || <span className="italic text-slate-400">No raw notes.</span>}
                      {(c.discovery_questions || []).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <div className="text-[10px] font-mono uppercase text-slate-500 mb-1">Clarifying questions</div>
                          <ol className="list-decimal list-inside space-y-1">
                            {c.discovery_questions.map((q, i) => <li key={i}>{q}</li>)}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Discovery Timeline (Feb 2026) — every status change, actor + timestamp */}
              <div className="mt-3">
                <button
                  onClick={() => toggleTimeline(c)}
                  data-testid={`toggle-timeline-${c.id}`}
                  className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#0F1E38]"
                >
                  {timelineOpen[c.id] ? <CaretDown size={11} /> : <CaretRight size={11} />}
                  <Calendar size={11} /> Discovery Timeline
                </button>
                {timelineOpen[c.id] && (
                  <div className="mt-2 p-3 rounded bg-slate-50 border border-slate-200 max-h-64 overflow-y-auto" data-testid={`timeline-${c.id}`}>
                    {(timelines[c.id] || []).length === 0 ? (
                      <div className="text-[11px] text-slate-500 italic">No events yet.</div>
                    ) : (
                      <ol className="space-y-2 text-[11px]">
                        {(timelines[c.id] || []).map((ev, i) => (
                          <li key={i} className="flex gap-3">
                            <div className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${ev.source === "outbound" ? "bg-blue-500" : ev.source === "inbound" ? "bg-emerald-500" : "bg-violet-500"}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-slate-800 font-medium">{fmtTimelineKind(ev.kind)}</div>
                              <div className="font-mono text-slate-500 text-[10px]">
                                {ev.created_at ? new Date(ev.created_at).toLocaleString() : "—"}
                                {" · "}
                                <span className="uppercase">{ev.source}</span>
                                {ev.ok === false && <span className="text-red-600"> · failed{ev.error ? ": " + ev.error.slice(0, 40) : ""}</span>}
                              </div>
                              {ev.payload?.feedback && (
                                <div className="text-slate-600 mt-1 italic">"{ev.payload.feedback}"</div>
                              )}
                              {ev.payload?.round && ev.payload.round > 1 && (
                                <div className="text-[10px] font-mono text-amber-700 uppercase mt-0.5">Round {ev.payload.round}</div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </div>

              {/* Intake links (persist across page loads — always copyable) */}
              {invs.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-2">
                    Intake links · {invs.length} · {submitted} submitted
                  </div>
                  <div className="space-y-1.5">
                    {invs.map((inv) => (
                      <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="flex items-center gap-2 text-[10px] font-mono">
                        {inv.status === "submitted" ? (
                          <CheckCircle size={13} weight="fill" className="text-emerald-500 shrink-0" />
                        ) : (
                          <LinkSimple size={13} className="text-amber-500 shrink-0" />
                        )}
                        <span className="truncate text-slate-700 flex-1">{inv.project_title || "Untitled"}</span>
                        {inv.status === "submitted" ? (
                          <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">submitted</span>
                        ) : (
                          <button
                            data-testid={`copy-invite-${inv.id}`}
                            onClick={() => copyLink(inv.token)}
                            className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                            title={buildLink(inv.token)}
                          >
                            <Copy size={10} className="inline mr-1" /> Copy link
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {clients.length === 0 && <div className="grid-bg py-20 text-center text-sm text-slate-500 rounded border border-slate-200">No clients yet — kick off a discovery from the Leads tab or add one directly.</div>}

      {/* Schedule Discovery dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg" data-testid="schedule-discovery-dialog">
          <DialogHeader><DialogTitle>Schedule Discovery · {scheduleFor?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              This marks the discovery meeting as booked and pushes a job to PSDoc so the presale engineer can capture notes there.
              You'll get the notes back once they submit — then the <b>Send Intake</b> button unlocks.
            </p>
            <div>
              <Label>Meeting date/time (optional)</Label>
              <Input
                type="datetime-local"
                value={scheduleForm.meeting_at}
                onChange={(e) => setScheduleForm({ ...scheduleForm, meeting_at: e.target.value })}
                className="bg-slate-50 border-slate-300 mt-1 font-mono"
                data-testid="schedule-meeting-at"
              />
            </div>
            <div>
              <Label>Note for presale (optional)</Label>
              <Textarea
                rows={3}
                value={scheduleForm.note}
                onChange={(e) => setScheduleForm({ ...scheduleForm, note: e.target.value })}
                placeholder="e.g. Meeting is with their CTO; they've asked us to focus on SD-WAN options and cutover strategy."
                className="bg-slate-50 border-slate-300 mt-1"
                data-testid="schedule-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)} className="border-slate-300">Cancel</Button>
            <Button onClick={submitScheduleDiscovery} data-testid="schedule-submit" className="bg-[#FFC107] hover:bg-[#E5A906]">Schedule + Push to PSDoc</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Intake Link dialog (no auto-email) */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg" data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Create Intake Link · {inviteFor?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 text-slate-700">
              We won't email the client automatically. After you click <b>Create Link</b>, copy the link from the client card and paste it into your Outlook email. This keeps the reply-to correct.
            </div>
            <div><Label>Project Title</Label><Input data-testid="invite-title" value={inviteForm.project_title} onChange={(e) => setInviteForm({...inviteForm, project_title: e.target.value})} className="bg-slate-50 border-slate-300 mt-1" placeholder="e.g. Branch office SD-WAN rollout"/></div>
            <div><Label>Internal Note (optional)</Label><Textarea rows={2} value={inviteForm.note} onChange={(e) => setInviteForm({...inviteForm, note: e.target.value})} className="bg-slate-50 border-slate-300 mt-1" placeholder="Only visible to you — shows above the intake form."/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="border-slate-300">Cancel</Button>
            <Button data-testid="create-invite-btn" onClick={sendInvite} className="bg-[#FFC107] hover:bg-[#E5A906]">Create Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Discovery dialog — replaces the old "delete lead" hack. Records a reason,
          pushes 'discovery_cancelled' to PSDoc so their board mirrors, leaves the lead untouched. */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg" data-testid="cancel-discovery-dialog">
          <DialogHeader><DialogTitle>Cancel Discovery · {cancelFor?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs bg-red-50 border border-red-200 rounded p-3 text-red-800">
              This flips the discovery to <b>Cancelled</b> and mirrors the cancellation to PSDoc so their board closes the job too. The Lead itself is left untouched — you can start a fresh discovery on the same lead later if the customer comes back.
            </div>
            <div>
              <Label>Category</Label>
              <Select value={cancelForm.category} onValueChange={(v) => setCancelForm({ ...cancelForm, category: v })}>
                <SelectTrigger className="bg-slate-50 border-slate-300 mt-1" data-testid="cancel-category-trigger"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {CANCEL_CATEGORIES.map(o => <SelectItem key={o.key} value={o.key} data-testid={`cancel-category-${o.key}`}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                rows={3}
                value={cancelForm.reason}
                onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                placeholder="e.g. Customer said budget is postponed until FY27. Will re-engage in Jan."
                className="bg-slate-50 border-slate-300 mt-1"
                data-testid="cancel-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="border-slate-300">Back</Button>
            <Button
              onClick={submitCancelDiscovery}
              data-testid="cancel-discovery-submit"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Cancel Discovery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
